import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import CountryCode from "@mapmycustomers/shared/enum/CountryCode";
import BaseMapStyle from "@mapmycustomers/shared/enum/map/BaseMapStyle";
import CumulDashboard from "@mapmycustomers/shared/types/cumul/CumulDashboard";
import { EntityTypesSupportingFieldCustomization } from "@mapmycustomers/shared/types/entity";
import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import Organization, { OrganizationMetaData } from "@mapmycustomers/shared/types/Organization";
import { OrganizationSize } from "@mapmycustomers/shared/types/OrganizationSize";
import Permission from "@mapmycustomers/shared/types/Permission";
import PersistentFieldInsights from "@mapmycustomers/shared/types/persistent/PersistentFieldInsights";
import Plan from "@mapmycustomers/shared/types/Plan";
import Role from "@mapmycustomers/shared/types/Role";
import Sandbox from "@mapmycustomers/shared/types/Sandbox";
import Setting from "@mapmycustomers/shared/types/Setting";
import User from "@mapmycustomers/shared/types/User";
import SavedFilter from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import { OnlyRequiredFields } from "@mapmycustomers/shared/util/ts";

import Iam from "@app/types/Iam";

export const fetchMe = createAsyncAction(
  "@iam/fetchMe/request",
  "@iam/fetchMe/success",
  "@iam/fetchMe/failure"
)<undefined, Iam | undefined, unknown>();

export const fetchOrgSandbox = createAsyncAction(
  "@iam/fetchOrgSandbox/request",
  "@iam/fetchOrgSandbox/success",
  "@iam/fetchOrgSandbox/failure"
)<void, Sandbox | undefined, unknown>();

export const fetchPermissions = createAsyncAction(
  "@iam/fetchPermissions/request",
  "@iam/fetchPermissions/success",
  "@iam/fetchPermissions/failure"
)<Role["id"], Permission[], void>();

export const findMe = createAsyncAction(
  "@iam/findMe/request",
  "@iam/findMe/success",
  "@iam/findMe/failure"
)<void, GeolocationPosition, void>();

export const getUserCountry = createAsyncAction(
  "@iam/getUserCountry/request",
  "@iam/getUserCountry/success",
  "@iam/getUserCountry/failure"
)<void, CountryCode, void>();

export const changePassword = createAsyncAction(
  "@iam/changePassword/request",
  "@iam/changePassword/success",
  "@iam/changePassword/failure"
)<{ currentPassword: string; password: string }, void, unknown>();
export const showChangePasswordModal = createAction("@iam/changePassword/showModal")<void>();
export const hideChangePasswordModal = createAction("@iam/changePassword/hideModal")<void>();

export const uploadProfilePhoto = createAsyncAction(
  "@iam/uploadProfilePhoto/request",
  "@iam/uploadProfilePhoto/success",
  "@iam/uploadProfilePhoto/failure"
)<Blob | File, User, unknown>();

export const updateMe = createAsyncAction(
  "@iam/updateMe/request",
  "@iam/updateMe/success",
  "@iam/updateMe/failure"
)<{ callback?: (me: Iam) => void; me: Iam }, Iam, unknown>();

export const updateOrganization = createAsyncAction(
  "@iam/updateOrganization/request",
  "@iam/updateOrganization/success",
  "@iam/updateOrganization/failure"
)<
  {
    callback?: () => void;
    organization: OnlyRequiredFields<Organization, "id">;
    settings?: Setting[];
  },
  Iam,
  unknown
>();

// if you need to change many settings at once, use updateOrganization action instead
export const updateOrganizationSetting = createAsyncAction(
  "@iam/updateOrganizationSetting/request",
  "@iam/updateOrganizationSetting/success",
  "@iam/updateOrganizationSetting/failure"
)<{ callback?: () => void; setting: Setting }, Iam, unknown>();

export const deleteProfilePhoto = createAsyncAction(
  "@iam/deleteProfilePhoto/request",
  "@iam/deleteProfilePhoto/success",
  "@iam/deleteProfilePhoto/failure"
)<void, User, unknown>();

export const showDeleteProfilePhotoModal = createAction("@iam/deleteProfilePhotoModal/show")();
export const hideDeleteProfilePhotoModal = createAction("@iam/deleteProfilePhotoModal/hide")();

export const updateSetting = createAsyncAction(
  "@iam/updateSetting/request",
  "@iam/updateSetting/success",
  "@iam/updateSetting/failure"
)<
  {
    callback?: (setting: Setting) => void;
    orgId: Organization["id"];
    setting: Setting;
    userId: User["id"];
  },
  Setting,
  unknown
>();

export const updateOrganizationMetadata = createAsyncAction(
  "@iam/updateOrganizationMetadata/request",
  "@iam/updateOrganizationMetadata/success",
  "@iam/updateOrganizationMetadata/failure"
)<
  {
    callback?: (updatedMetaData?: OrganizationMetaData) => void;
    metaData: Partial<OrganizationMetaData>;
  },
  Organization,
  unknown
>();

export const updateMetadata = createAsyncAction(
  "@iam/updateUserMetadata/request",
  "@iam/updateUserMetadata/success",
  "@iam/updateUserMetadata/failure"
)<Partial<User["metaData"]>, User["metaData"], unknown>();

export const updateFieldInsightsMetaData = createAction("@iam/updateFieldInsightsMetaData")<{
  callback?: () => void;
  failureCallback?: () => void;
  metaData: Partial<PersistentFieldInsights>;
}>();

export const addFavoriteSavedFilter = createAsyncAction(
  "@iam/addFavoriteSavedFilter/request",
  "@iam/addFavoriteSavedFilter/success",
  "@iam/addFavoriteSavedFilter/failure"
)<SavedFilter["id"], User, void>();

export const removeFavoriteSavedFilters = createAsyncAction(
  "@iam/removeFavoriteSavedFilters/request",
  "@iam/removeFavoriteSavedFilters/success",
  "@iam/removeFavoriteSavedFilters/failure"
)<SavedFilter["id"][], User, void>();

export const addOrgFavoriteSavedFilter = createAsyncAction(
  "@iam/addOrgFavoriteSavedFilter/request",
  "@iam/addOrgFavoriteSavedFilter/success",
  "@iam/addOrgFavoriteSavedFilter/failure"
)<SavedFilter["id"], Organization, void>();

export const removeOrgFavoriteSavedFilter = createAsyncAction(
  "@iam/removeOrgFavoriteSavedFilter/request",
  "@iam/removeOrgFavoriteSavedFilter/success",
  "@iam/removeOrgFavoriteSavedFilter/failure"
)<SavedFilter["id"], Organization, void>();

export const togglePinnedDashboard = createAsyncAction(
  "@iam/togglePinnedDashboard/request",
  "@iam/togglePinnedDashboard/success",
  "@iam/togglePinnedDashboard/failure"
)<CumulDashboard["id"], User, unknown>();

export const setCumulPromotionalUiVisited = createAsyncAction(
  "@iam/setCumulPromotionalUiVisited/request",
  "@iam/setCumulPromotionalUiVisited/success",
  "@iam/setCumulPromotionalUiVisited/failure"
)<void, User, unknown>();

export const fetchGeocodingLimit = createAsyncAction(
  "@iam/fetchGeocodingLimit/request",
  "@iam/fetchGeocodingLimit/success",
  "@iam/fetchGeocodingLimit/failure"
)<
  void,
  {
    geocodingMmcLimitReached: boolean;
    geocodingOrgLimit: number;
    geocodingOrgLimitReached: boolean;
  },
  unknown
>();

export const updateLastUsedLayout = createAction("@iam/updateLastUsedLayout")<{
  entityType: EntityTypesSupportingFieldCustomization;
  layoutId: FormLayout["id"];
}>();

export const setOrganizationSize = createAction("@iam/setOrganizationSize")<OrganizationSize>();

export const setOrgSandbox = createAction("@iam/setOrgSandbox")<Sandbox>();

export const updateDefaultColumns = createAction("@iam/updateDefaultColumns")<{
  callback?: () => void;
  defaultColumns: OrganizationMetaData["defaultColumns"];
}>();

export const updateFlowStatus = createAsyncAction(
  "@iam/updateFlowStatus/request",
  "@iam/updateFlowStatus/success",
  "@iam/updateFlowStatus/failure"
)<{ slug: string; status: any }, User["metaData"], void>();

export const resetFlowStatuses = createAsyncAction(
  "@iam/resetFlowStatuses/request",
  "@iam/resetFlowStatuses/success",
  "@iam/resetFlowStatuses/failure"
)<void, User["metaData"], void>();

export const fetchPlans = createAsyncAction(
  "@iam/fetchPlans/request",
  "@iam/fetchPlans/success",
  "@iam/fetchPlans/failure"
)<void, Plan[], void>();

export const fetchUsageLimits = createAsyncAction(
  "@iam/fetchUsageLimits/request",
  "@iam/fetchUsageLimits/success",
  "@iam/fetchUsageLimits/failure"
)<void, Iam["usageLimits"], void>();

// increments or decrements current value of records usage
export const updateRecordUsage = createAction("@iam/updateRecordUsage")<number>();

// increments or decrements current value of custom fields usage
export const updateCustomFieldsUsage = createAction("@iam/updateCustomFieldsUsage")<number>();

export const updatePreferredMapStyle = createAction("@iam/updatePreferredMapStyle")<BaseMapStyle>();

export type Actions = ActionType<
  | typeof addFavoriteSavedFilter
  | typeof addOrgFavoriteSavedFilter
  | typeof changePassword
  | typeof deleteProfilePhoto
  | typeof fetchGeocodingLimit
  | typeof fetchMe
  | typeof fetchOrgSandbox
  | typeof fetchPermissions
  | typeof fetchPlans
  | typeof fetchUsageLimits
  | typeof findMe
  | typeof getUserCountry
  | typeof hideChangePasswordModal
  | typeof hideDeleteProfilePhotoModal
  | typeof removeFavoriteSavedFilters
  | typeof removeOrgFavoriteSavedFilter
  | typeof resetFlowStatuses
  | typeof setCumulPromotionalUiVisited
  | typeof setOrganizationSize
  | typeof setOrgSandbox
  | typeof showChangePasswordModal
  | typeof showDeleteProfilePhotoModal
  | typeof togglePinnedDashboard
  | typeof updateCustomFieldsUsage
  | typeof updateDefaultColumns
  | typeof updateFieldInsightsMetaData
  | typeof updateFlowStatus
  | typeof updateLastUsedLayout
  | typeof updateMe
  | typeof updateMetadata
  | typeof updateOrganization
  | typeof updateOrganizationMetadata
  | typeof updateOrganizationSetting
  | typeof updatePreferredMapStyle
  | typeof updateRecordUsage
  | typeof updateSetting
  | typeof uploadProfilePhoto
>;
